import { isEmpty, unique } from 'radash';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Callout } from '@/components/core/Callout';
import { Loader as CoreLoader } from '@/components/core/Loader';
import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { CategoryComponent } from '@/components/datamodel/Category/Category';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { useAuth } from '@/hooks/useAuth';
import { useDataModel } from '@/hooks/useDataModel';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import {
    DataModel,
    DataModelAttribute,
    DataModelAttributeTypeName,
    Item,
} from '@/types/DataModel';

const LAYOUT = 'edit';

interface PriorItemUpdateAttributeValuesProps {
    getTemplateAttributesMap: (category?: string) => { [key: string]: any };
    isCategorySpecific: boolean;
    preselectedCategory?: string;
    // Fired whenever the currently selected category has a non-empty template
    // map but none of the declared keys resolve to a known DataModelAttribute.
    // Used by the dialog to block Submit so the user is not handed a blank
    // editor with no diagnostic. See LAX-10101.
    onResolvableAttributesChange?: (hasResolvableAttributes: boolean) => void;
};

const PriorItemUpdateAttributeValues = ({
    getTemplateAttributesMap,
    isCategorySpecific,
    preselectedCategory,
    onResolvableAttributesChange,
}: PriorItemUpdateAttributeValuesProps) => {
    const { dataModel } = useDataModel();
    const { t } = useTranslation();
    const { auth } = useAuth();

    const { setValue, watch } = useFormContext();
    const currentSelectedCategory = watch('category__');
    const [selectedAttributes, setSelectedAttributes] = useState<DataModelAttribute[]>([]);

    useEffect(() => {
        const template = getTemplateAttributesMap(currentSelectedCategory);
        const attributes = loadAttributes(template, dataModel, currentSelectedCategory);
        attributes.forEach((attribute) => {
            const initialValue = template[attribute.name];
            setValue(attribute.name, initialValue);
        });
        setSelectedAttributes(attributes);

        // Silent-degrade guard (LAX-10101): if the data model directive
        // declared attributes for this category but none of them resolve
        // against the current data model, the dialog would otherwise render
        // with only the category selector and Submit enabled — the user's
        // implicit intent is then silently dropped. Warn with the unresolved
        // keys and let the caller block Submit.
        const configuredKeys = Object.keys(template);
        const resolvedNames = new Set(attributes.map((a) => a.name));
        const unresolvedKeys = configuredKeys.filter((k) => !resolvedNames.has(k));
        const hasResolvableAttributes = configuredKeys.length === 0 || attributes.length > 0;
        if (!hasResolvableAttributes) {
            const $angularJSInjector = getInjector();
            const $log = $angularJSInjector.get('$log');
            $log.warn(
                'PriorItemUpdateAttributeValues: template declared attributes '
                    + `[${unresolvedKeys.join(', ')}] for category "${currentSelectedCategory}", `
                    + 'but none resolved against the data model. Submit disabled.',
            );
        }
        onResolvableAttributesChange?.(hasResolvableAttributes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSelectedCategory]);

    if (!dataModel || !auth) {
        return null;
    }

    return !selectedAttributes ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-h-screen">
            <CoreLoader />
        </div>
    ) : (
        <div className="tw-flex-1 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
            <Callout className="tw-my-6" variant="info">
                {t('PRIOR_ITEM_DIALOG.DESCRIPTION')}
            </Callout>
            <div className="tw-flex-1 tw-h-full tw-overflow-y-auto">
                <LayoutProvider layoutName={LAYOUT}>
                    {isCategorySpecific ? (
                        <div className="tw-flex tw-flex-col tw-w-full tw-my-6">
                            <CategoryComponent
                                initialSelectedCategory={preselectedCategory}
                                disabled={!isEmpty(preselectedCategory)}
                                isNewItem={true}
                            />
                        </div>
                    ) : null}
                    <div className="tw-flex tw-flex-col tw-w-full tw-gap-3">
                        {selectedAttributes.map(
                            (attribute: DataModelAttribute, index: number) => (
                                <div
                                    className="tw-flex tw-flex-col tw-w-full"
                                    key={index}
                                >
                                    <AttributeComponent
                                        attribute={attribute}
                                        direction="horizontal"
                                    />
                                </div>
                            ),
                        )}
                    </div>
                </LayoutProvider>
            </div>
        </div>
    );
};
PriorItemUpdateAttributeValues.displayName = 'PriorItemUpdateAttributeValues';

const isComplexAttribute = (attribute: DataModelAttribute) => {
    return (
        attribute.typeName &&
        [
            DataModelAttributeTypeName.Collection,
            DataModelAttributeTypeName.Group,
            DataModelAttributeTypeName.MultiReference,
            DataModelAttributeTypeName.MultiDimensional,
            DataModelAttributeTypeName.EnumSet,
        ].includes(attribute.typeName)
    );
};

function loadAttributes(
    template: { [key: string]: any },
    dataModel?: DataModel,
    category?: string,
): DataModelAttribute[] {
    const _category = category ? {category__: category} as Item : undefined;
    const attributes =
        dataModel?.filteredLayoutAttributes(LAYOUT, _category) || [];

    let filteredAttributes: DataModelAttribute[] = attributes
        .filter((attribute) =>
            !isComplexAttribute(attribute) &&
            !dataModel?.isMetaAttribute(attribute.name),
        );

    filteredAttributes = unique(filteredAttributes, (a) => a.name);

    if (category) {
        const categoryAttributes = dataModel?.categoryAttributes(category) || [];
        filteredAttributes = filteredAttributes.filter(attribute =>
            categoryAttributes.some(categoryAttribute => categoryAttribute.name === attribute.name)
        );
    }

    const templateAttributes = Object.keys(template)
        .map((name) => dataModel?.attribute(name))
        .filter((attribute): attribute is DataModelAttribute => !isEmpty(attribute));

    filteredAttributes = filteredAttributes.filter((attribute) =>
        templateAttributes.some((attr) => attr.name === attribute.name),
    );

    // FIXME: remove this when we migrate the browse page
    // Grab $rootScope from the angularjs world
    const $angularJSInjector = getInjector();
    const $rootScope = getRootScope($angularJSInjector);
    $rootScope.prepareAttributes(filteredAttributes);

    return filteredAttributes;
}

export default PriorItemUpdateAttributeValues;
